import React, { useState } from 'react'
import { com } from '@eidu/form'
import { Typography } from '@mui/material'
import DraftFieldValue from '../../domain/entity/DraftFieldValue'
import useDebounced from '../../ui/debounce/useDebounced'
import usePaginatedEntitiesOfType from '../../io/usePaginatedEntitiesOfType'
import useEntityById from '../../io/useEntityById'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EqualityHashMap from '../../util/EqualityHashMap'
import hasFieldError from '../../domain/entity/hasFieldError'
import EntityFormReferenceInput from './EntityFormReferenceInput'
import EntityFormTextInput from './EntityFormTextInput'
import EntityFormDateInput from './EntityFormDateInput'
import EntityFormNumberInput from './EntityFormNumberInput'
import Field = com.eidu.sharedlib.entity.field.Field
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import entityIdFromStringOrNull = com.eidu.sharedlib.entity.entityIdFromStringOrNull
import EntityId = com.eidu.sharedlib.entity.EntityId
import FieldType = com.eidu.sharedlib.entity.field.FieldType

export type EntityFormInputProps = {
  field: Field
  fieldValue: DraftFieldValue | undefined
  entityTypes: ReadonlyMap<EntityTypeId, EntityType | null>
  updateField: (value: DraftFieldValue) => void
}

const EntityFormInput = ({ field, fieldValue, entityTypes, updateField }: EntityFormInputProps) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery] = useDebounced(searchQuery)

  const referenceableEntityTypeIds: EntityTypeId[] = Array.from(field.validReferenceTypes?.asJsReadonlySetView() ?? [])

  const referenceableEntityTypesById: Map<EntityTypeId, EntityType | null> = new EqualityHashMap(
    [...(entityTypes || [])].filter(([id]) => referenceableEntityTypeIds.some((it) => it.equals(id)))
  )

  // We only support one reference type per field at the moment. In the future, when we allow multiple, we need to change this behavior
  const firstReferenceTypeId = referenceableEntityTypeIds[0]
  const shouldLoadEntities = referenceableEntityTypeIds.length === 1 && firstReferenceTypeId !== undefined

  const {
    entities: referenceableEntities,
    loadMore,
    hasMore,
    loading,
  } = usePaginatedEntitiesOfType({
    typeId: firstReferenceTypeId ?? referenceableEntityTypeIds[0] ?? entityTypes.keys().next().value,
    searchQuery: debouncedSearchQuery,
    allTypes: entityTypes,
  })

  const referenceableEntitiesByTypeId: ReadonlyMap<
    EntityTypeId,
    ReadonlyMap<EntityId, EntityWithLabelAndRelated>
  > = shouldLoadEntities && firstReferenceTypeId
    ? new EqualityHashMap([
        [firstReferenceTypeId, new EqualityHashMap(referenceableEntities.map((e) => [e.entity.id, e]))],
      ])
    : new Map()

  const selectedId = fieldValue?.value ? entityIdFromStringOrNull(fieldValue.value) ?? undefined : undefined
  const { entity: selectedEntity } = useEntityById(selectedId)

  const augmentedReferenceableEntitiesByTypeId = (() => {
    if (!selectedEntity) return referenceableEntitiesByTypeId
    const { typeId } = selectedEntity.entity
    const existing: ReadonlyMap<EntityId, EntityWithLabelAndRelated> =
      referenceableEntitiesByTypeId.get(typeId) ?? new Map()
    const mergedForType = new EqualityHashMap([...existing.entries(), [selectedEntity.entity.id, selectedEntity]])
    return new EqualityHashMap([...referenceableEntitiesByTypeId.entries(), [typeId, mergedForType]])
  })()

  const error = hasFieldError(field, fieldValue, augmentedReferenceableEntitiesByTypeId)

  switch (field.type) {
    case FieldType.Text:
      return <EntityFormTextInput field={field} fieldValue={fieldValue} updateField={updateField} error={error} />
    case FieldType.Date:
      return <EntityFormDateInput field={field} fieldValue={fieldValue} updateField={updateField} error={error} />
    case FieldType.Number:
      return <EntityFormNumberInput field={field} fieldValue={fieldValue} updateField={updateField} error={error} />
    case FieldType.Reference:
      return (
        <EntityFormReferenceInput
          field={field}
          fieldValue={fieldValue}
          referenceableEntityTypesById={referenceableEntityTypesById}
          referenceableEntitiesByTypeId={referenceableEntitiesByTypeId}
          updateField={updateField}
          error={error}
          setSearchQuery={setSearchQuery}
          loadMore={loadMore}
          hasMore={hasMore}
          loading={loading}
        />
      )
    default:
      return (
        <Typography key={`field-${field.id.toString()}`}>
          Field &quot;{field.name}&quot; has unsupported type {field.type.name}
        </Typography>
      )
  }
}

export default EntityFormInput
